import classNames from 'classnames';
import React, { useMemo } from 'react';

import ForwardIcon from '@mui/icons-material/ArrowForwardIos';
import { makeStyles } from '@mui/styles';

import { translate } from '@vestahealthcare/common/i18n';
import {
  ClinicalMember,
  MemberDashboardTask,
  PaginationType,
  Patient,
} from '@vestahealthcare/common/models';

import { Card, Chip, Colors, Fonts } from 'styleguide-v2';

import Session from 'dash/src/services/SessionServices';

import { OpenTasksTable } from './OpenTasksTable';

type Props = {
  className?: string;
  data: MemberDashboardTask[];
  member: Patient | ClinicalMember;
  onSeeMore: () => Promise<void>;
  openTasks?: number;
  pagination?: PaginationType;
};

const useStyles = makeStyles({
  container: {
    '&&': {
      borderRadius: '8px',
    },

    display: 'flex',

    '& > div': {
      flex: 1,
      padding: '2.4rem 3.2rem',
    },
  },
  subContainer: {
    display: 'flex',
    flexFlow: 'column',
  },
  title: {
    alignItems: 'center',
    display: 'flex',

    '&&': {
      fontFamily: Fonts.fontFamily,
      fontSize: `calc(${Fonts.fontSize} * 1.25)`,
      fontWeight: 500,
      margin: 0,
    },
  },
});

export const OpenTasksCard = ({
  className,
  data,
  member,
  onSeeMore,
  openTasks,
  pagination,
}: Props) => {
  const styles = useStyles();

  const memberId = useMemo(
    () => (member instanceof Patient ? member?.id : member.memberId),
    [member],
  );
  const openNewTab = useMemo(() => !(member instanceof Patient), [member]);
  const shouldShowLink = Session.actingUser.showMemberLink;

  return (
    <Card className={classNames(className, styles.container)}>
      <div className={styles.subContainer}>
        <div className="flex spaced">
          <span className={styles.title}>
            {translate('memberProfile.dashboard.openTasks')}
            {!!openTasks && (
              <>
                &nbsp;&nbsp;
                <Chip color="error" type="contained-2">
                  <span className="bold">{openTasks} OPEN</span>
                </Chip>
              </>
            )}
          </span>
          {shouldShowLink && (
            <a
              className="flex middle min-gap"
              href={`#/patients/${memberId}/tasks`}
              style={{ color: Colors.textGreen, fontWeight: 500 }}
              rel="noreferrer"
              target={openNewTab ? '_blank' : undefined}
            >
              {translate('memberProfile.dashboard.goToTasks')}
              <ForwardIcon fontSize="small" />
            </a>
          )}
        </div>
        <br />
        <OpenTasksTable
          data={data}
          onSeeMore={onSeeMore}
          pagination={pagination}
          shouldShowLink={shouldShowLink}
        />
      </div>
    </Card>
  );
};
